'use client';

import { useHeaderContext } from '@/components/Header/provider';
import { parseLink } from '@/components/Header/utils';
import { ImageMedia } from '@/components/Media/ImageMedia';
import RichText from '@/components/RichText';
import { type IntroBlock } from '@/payload/payload.types';
import { Button } from '@tallinn-arvudes/ui/tallinn-design/Button';
import { cn } from '@tallinn-arvudes/ui/utils';
import { useRouter } from 'next/navigation';

export const IntroBlockComponent = (props: {
  columns: IntroBlock['columns'];
  title: IntroBlock['title'];
  borderBottom?: boolean;
}) => {
  const { push } = useRouter();
  const { onValueChange } = useHeaderContext();
  const { columns, borderBottom, title } = props;

  if (!columns || !columns.length) {
    return null;
  }

  return (
    <div
      className={cn('flex w-full justify-center', {
        'border-b-[1px] border-semantic-border-moderate': borderBottom,
      })}
    >
      <div className="px-6 md:px-8 py-6 md:py-10 flex flex-col max-w-content w-full gap-10">
        {title ? (
          <h2 className="tds-heading-lg text-semantic-content-brand">
            {title}
          </h2>
        ) : null}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-8 text-semantic-content w-full">
          {columns.map((item) => {
            if (!item) {
              return null;
            }

            const link = parseLink(item.link);

            return (
              <div
                key={item.id}
                className={cn('flex flex-col', {
                  'group cursor-pointer':
                    item.includeLink && (link?.href || link?.headerKey),
                  'max-h-fit': item.includeLink && item.placeUnderContent,
                })}
                onClick={() => {
                  if (!item.includeLink) {
                    return;
                  }

                  if (link?.headerKey) {
                    onValueChange(link.headerKey);
                    return;
                  }
                  if (link?.href) {
                    push(link.href);
                  }
                }}
              >
                {item.image && (
                  <div className="mb-4">
                    <ImageMedia resource={item.image} />
                  </div>
                )}
                {item.title ? (
                  <h2 className="tds-heading-sm font-bold mb-3 group-hover:text-semantic-content-action-hover">
                    {item.title}
                  </h2>
                ) : null}
                {item.description && (
                  <div className="mb-4 flex-grow">
                    <RichText
                      data={item.description}
                      className="group-hover:text-semantic-content-action-hover"
                    />
                  </div>
                )}
                {item.includeLink && link ? (
                  <Button
                    variant="secondary"
                    compact
                    href={link.href!}
                    className="max-w-fit"
                    onClick={(e: { preventDefault: () => void }) => {
                      if (link?.headerKey) {
                        e.preventDefault();
                        onValueChange(link.headerKey);
                      }
                    }}
                  >
                    {link.name}
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
